'use client';

/**
 * BarChart Component
 *
 * Theme-aware bar chart with glassmorphism tooltips.
 * Built on Recharts with design system integration.
 *
 * @example
 * ```tsx
 * <BarChart
 *   data={data}
 *   bars={[
 *     { dataKey: 'sales', name: 'Sales', color: 'primary' },
 *     { dataKey: 'returns', name: 'Returns', color: 'error' },
 *   ]}
 *   xAxisKey="month"
 * />
 * ```
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  Legend,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';
import { type NeonAccent, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type ChartColorKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export interface BarConfig {
  /**
   * Data key for this bar
   */
  dataKey: string;

  /**
   * Display name for legend/tooltip
   */
  name?: string;

  /**
   * Color key or custom hex color
   */
  color?: ChartColorKey | string;

  /**
   * Bar radius (rounded corners)
   * @default [4, 4, 0, 0]
   */
  radius?: number | [number, number, number, number];

  /**
   * Stack ID for stacked bars
   */
  stackId?: string;

  /**
   * Bar fill opacity
   * @default 1
   */
  fillOpacity?: number;

  /**
   * Use gradient fill
   * @default false
   */
  gradientFill?: boolean;
}

export interface BarChartProps {
  /**
   * Chart data
   */
  data: Record<string, unknown>[];

  /**
   * Bar configurations
   */
  bars: BarConfig[];

  /**
   * Data key for X-axis
   */
  xAxisKey: string;

  /**
   * Chart height
   * @default 300
   */
  height?: number;

  /**
   * Bar layout
   * @default 'vertical'
   */
  layout?: 'vertical' | 'horizontal';

  /**
   * Show grid lines
   * @default true
   */
  showGrid?: boolean;

  /**
   * Show X-axis
   * @default true
   */
  showXAxis?: boolean;

  /**
   * Show Y-axis
   * @default true
   */
  showYAxis?: boolean;

  /**
   * Show legend
   * @default false
   */
  showLegend?: boolean;

  /**
   * Show tooltip
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Optional neon accent for styling
   */
  neonAccent?: NeonAccent;

  /**
   * Bar gap (pixels)
   * @default 4
   */
  barGap?: number;

  /**
   * Category gap (percentage)
   * @default '20%'
   */
  barCategoryGap?: string | number;

  /**
   * Maximum bar size (pixels)
   * @default 60
   */
  maxBarSize?: number;

  /**
   * Y-axis domain
   */
  yAxisDomain?: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax'];

  /**
   * X-axis tick formatter
   */
  xAxisFormatter?: (value: unknown) => string;

  /**
   * Y-axis tick formatter
   */
  yAxisFormatter?: (value: number) => string;

  /**
   * Tooltip value formatter
   */
  tooltipFormatter?: (value: number, name: string) => [string, string];

  /**
   * Chart margins (all values required in recharts v3)
   */
  margin?: { top: number; right: number; bottom: number; left: number };
}

// ============================================================================
// Styled Components
// ============================================================================

interface TooltipContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const TooltipContainer = styled(
  Box,
  transientOptions,
)<TooltipContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonColors = $neonAccent ? neonAccents[$neonAccent] : null;

  return {
    backgroundColor: neonColors ? ($isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)') : glass.background,
    border: `1px solid ${neonColors ? ($isDark ? neonColors.border : neonColors.borderLight) : glass.border}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    backdropFilter: 'blur(12px)',
    boxShadow: $isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
  };
});

// ============================================================================
// Custom Tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    name: string;
    color: string;
    dataKey: string;
  }>;
  label?: string;
  isDark: boolean;
  neonAccent?: NeonAccent;
  formatter?: (value: number, name: string) => [string, string];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, isDark, neonAccent, formatter }) => {
  if (!active || !payload?.length) return null;

  return (
    <TooltipContainer $isDark={isDark} $neonAccent={neonAccent}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {payload.map((entry, index) => {
        const [formattedValue, formattedName] = formatter
          ? formatter(entry.value, entry.name)
          : [String(entry.value), entry.name];

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: index > 0 ? 0.5 : 0,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mr: 1,
              }}
            >
              {formattedName}:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {formattedValue}
            </Typography>
          </Box>
        );
      })}
    </TooltipContainer>
  );
};

// ============================================================================
// Component
// ============================================================================

export const BarChart: React.FC<BarChartProps> = ({
  data,
  bars,
  xAxisKey,
  height = 300,
  layout = 'vertical',
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showLegend = false,
  showTooltip = true,
  neonAccent,
  barGap = 4,
  barCategoryGap = '20%',
  maxBarSize = 60,
  yAxisDomain,
  xAxisFormatter,
  yAxisFormatter,
  tooltipFormatter,
  margin = { top: 10, right: 30, left: 0, bottom: 0 },
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = useChartColors();

  // Resolve color from key or use custom hex
  const resolveColor = (colorKey?: ChartColorKey | string): string => {
    if (!colorKey) return colors.primary;
    if (colorKey.startsWith('#')) return colorKey;
    return colors[colorKey as ChartColorKey] || colors.primary;
  };

  // Generate stable gradient IDs (memoized to prevent re-renders)
  const gradientIds = useMemo(
    () =>
      bars.reduce(
        (acc, bar) => {
          acc[bar.dataKey] = `bar-gradient-${bar.dataKey}`;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [bars],
  );

  const isHorizontal = layout === 'horizontal';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart
        data={data}
        margin={margin}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        barGap={barGap}
        barCategoryGap={barCategoryGap}
      >
        {/* Gradient Definitions */}
        <defs>
          {bars
            .filter((bar) => bar.gradientFill)
            .map((bar) => {
              const color = resolveColor(bar.color);
              return (
                <linearGradient
                  key={bar.dataKey}
                  id={gradientIds[bar.dataKey]}
                  x1="0"
                  y1="0"
                  x2={isHorizontal ? '1' : '0'}
                  y2={isHorizontal ? '0' : '1'}
                >
                  <stop offset="5%" stopColor={color} stopOpacity={1} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.6} />
                </linearGradient>
              );
            })}
        </defs>

        {/* Grid */}
        {showGrid && (
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colors.grid}
            horizontal={!isHorizontal}
            vertical={isHorizontal}
          />
        )}

        {/* Axes */}
        {isHorizontal ? (
          <>
            {showYAxis && (
              <YAxis
                dataKey={xAxisKey}
                type="category"
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 12 }}
                tickLine={{ stroke: colors.grid }}
                axisLine={{ stroke: colors.grid }}
                tickFormatter={xAxisFormatter}
              />
            )}
            {showXAxis && (
              <XAxis
                type="number"
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 12 }}
                tickLine={{ stroke: colors.grid }}
                axisLine={{ stroke: colors.grid }}
                domain={yAxisDomain}
                tickFormatter={yAxisFormatter}
              />
            )}
          </>
        ) : (
          <>
            {showXAxis && (
              <XAxis
                dataKey={xAxisKey}
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 12 }}
                tickLine={{ stroke: colors.grid }}
                axisLine={{ stroke: colors.grid }}
                tickFormatter={xAxisFormatter}
              />
            )}
            {showYAxis && (
              <YAxis
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 12 }}
                tickLine={{ stroke: colors.grid }}
                axisLine={{ stroke: colors.grid }}
                domain={yAxisDomain}
                tickFormatter={yAxisFormatter}
              />
            )}
          </>
        )}

        {/* Tooltip */}
        {showTooltip && (
          <Tooltip
            content={<CustomTooltip isDark={isDark} neonAccent={neonAccent} formatter={tooltipFormatter} />}
            cursor={{
              fill: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            }}
          />
        )}

        {/* Legend */}
        {showLegend && (
          <Legend
            wrapperStyle={{
              paddingTop: 16,
            }}
            formatter={(value) => <span style={{ color: colors.text }}>{value}</span>}
          />
        )}

        {/* Bars */}
        {bars.map((bar) => {
          const color = resolveColor(bar.color);
          const fill = bar.gradientFill ? `url(#${gradientIds[bar.dataKey]})` : color;

          return (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name || bar.dataKey}
              fill={fill}
              fillOpacity={bar.fillOpacity || 1}
              radius={bar.radius ?? [4, 4, 0, 0]}
              stackId={bar.stackId}
              maxBarSize={maxBarSize}
            />
          );
        })}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

BarChart.displayName = 'BarChart';

export default BarChart;
